/**
 * BrandedLoader — HiroJet "Orbital Pulse" loading component
 * 
 * Variants:
 *   "page"    — Full-page centered (initial page loads, route transitions)
 *   "section" — Section-level centered (data loading within a page)
 *   "inline"  — Small inline spinner (buttons, table cells)
 */

import { BrandMark } from '@/components/Brand';


export function BrandedLoader({ variant = 'section', label, className = '' }) {
  if (variant === 'inline') {
    return (
      <span className={`inline-flex items-center gap-2 ${className}`} data-testid="branded-loader-inline">
        <span className="branded-spinner branded-spinner--sm" />
      </span>
    );
  }

  if (variant === 'page') {
    return (
      <div className={`flex items-center justify-center min-h-[60vh] w-full ${className}`} data-testid="branded-loader-page">
        <div className="flex flex-col items-center gap-6">
          <div className="relative">
            {/* Orbital dots */}
            <div className="absolute inset-[-60px] pointer-events-none" style={{ animation: 'wordmark-fade-in 0.8s ease 0.2s both' }}>
              <OrbitalDot index={0} radius={78} duration={3} size={7} color="#38BDF8" />
              <OrbitalDot index={1} radius={62} duration={2.4} size={5} color="#22D3EE" reverse />
              <OrbitalDot index={2} radius={50} duration={1.8} size={4} color="#67E8F9" />
            </div>
            {/* Logo */}
            <div className="branded-logo-ring branded-logo-ring--lg">
              <BrandMark className="w-20 h-20 relative z-10" />
            </div>
          </div>
          {label ? (
            <p className="text-sm text-ink-2 tracking-[0.35em] uppercase font-medium">{label}</p>
          ) : (
            <p className="text-sm text-ink-2 tracking-[0.35em] uppercase font-medium" style={{ animation: 'wordmark-fade-in 0.6s ease 0.5s forwards', opacity: 0 }}>HiroJet</p>
          )}
        </div>
      </div>
    );
  }

  // Default: section
  return (
    <div className={`flex items-center justify-center py-16 w-full ${className}`} data-testid="branded-loader-section">
      <div className="flex flex-col items-center gap-4">
        <div className="relative">
          <div className="absolute inset-[-28px] pointer-events-none">
            <OrbitalDot index={0} radius={38} duration={2.8} size={4} color="#38BDF8" />
            <OrbitalDot index={1} radius={28} duration={2.2} size={3} color="#22D3EE" reverse />
          </div>
          <div className="branded-logo-ring branded-logo-ring--sm">
            <BrandMark className="w-7 h-7 relative z-10" />
          </div>
        </div>
        {label && <p className="text-xs text-slate-500/50 tracking-wider">{label}</p>}
      </div>
    </div>
  );
}

/** CSS-driven orbital dot — zero JS overhead */
function OrbitalDot({ radius, duration, size, color, reverse, index }) {
  const startAngle = index * 120;
  return (
    <span
      className="absolute rounded-full"
      style={{
        width: size,
        height: size,
        top: '50%',
        left: '50%',
        marginTop: -size / 2,
        marginLeft: -size / 2,
        background: color,
        boxShadow: `0 0 ${size + 3}px ${color}66`,
        opacity: 0.8 - index * 0.15,
        animation: `orbit-generic ${duration}s linear infinite${reverse ? ' reverse' : ''}`,
        '--orbit-r': `${radius}px`,
        '--orbit-start': `${startAngle}deg`,
      }}
    />
  );
}

export function PageLoader() {
  return <BrandedLoader variant="page" />;
}

export default BrandedLoader;
